// Mirrors backend/src/pdfTemplates/designA/salesInsuranceAdapter.js's
// buildSalesInsuranceConfigDesignA exactly (same fields, same "Grand Total
// only" rule for individual sales, no fabricated tax-summary table) --
// used by SalesVoucherBodyDesignA.jsx when the fetched sale turns out to be
// insurance-billed (sale.insurance_id set), the same branch
// administration.js's generateSalesInvoicePdfFast makes server-side. Kept
// as a plain function (not a component) so it can be called from inside
// SalesVoucherBodyDesignA's existing data-fetching flow instead of
// duplicating a second sale_id fetch in a separate wrapper component.
import { numberFormat, formatToCompanyTimezone } from "../../../lib/functions";
import { convertAmountToWordsDesignA } from "../../../lib/designAWords";
import { fitItemName } from "../../../lib/designAItemFit";
import { formatPercent } from "../../../lib/designANumberFormat";

const isBonusItem = (item) => Number(item.item_rate) === 0;

// Row budgets recalibrated for the taller "rich item" row (name line + an
// optional description sub-line, see da-table--rich-item in
// VoucherBodyDesignA.css) -- ~42px/row vs. the default 28px, so ~2/3 as many
// rows fit per page. Mirrors salesInvoiceAdapter.js's own recalibration.
const SALES_INSURANCE_ROW_BUDGETS = { single: 10, first: 16, middle: 20, last: 9 };

// Same fixed-layout column widths as salesInsuranceAdapter.js -- see that
// file's comment for the reasoning. Keep both in sync.
const PAGE_CONTENT_WIDTH = 698;
const COL_WIDTH = { hash: 28, hsn: 72, rate: 100, qty: 44, disc: 64, tax: 54, amount: 104 };
const CELL_PADDING = 16;
const CHAR_WIDTH_PX = 5.3;

function ItemNameCell({ name, maxChars }) {
  const fit = fitItemName(name, { maxChars });
  return <span className="da-item-name" style={{ fontSize: `${fit.fontSizePercent}%` }}>{fit.text}</span>;
}

function ItemCell({ name, maxChars, description }) {
  return (
    <div className="da-item-cell">
      <ItemNameCell name={name} maxChars={maxChars} />
      {description && <div className="da-item-sub">{description}</div>}
    </div>
  );
}

export function buildSalesInsuranceConfigDesignA({ sale, accDue, authInfo, institutionData, institution, bankDetails, showStamp = false, showSignature = false, showShipping = false }) {
  const currency = authInfo?.currency;
  const country = authInfo?.country || "";
  const isIndia = country.toLowerCase() === "india";
  // Preserves SalesInsuranceVoucher.jsx's own (likely unintentional)
  // truthiness check -- `authInfo?.gst_enabled` is truthy for "yes" OR "no"
  // -- rather than "fixing" a behavior nobody asked to change.
  const isIndiaGst = !!authInfo?.gst_enabled;
  const taxLabel = isIndia ? "GST" : "VAT";
  const isGSTDisabled = isIndia && authInfo?.gst_enabled == "no";
  const isUAE = country.trim().toLowerCase() === "united arab emirates";

  const isIndividual = sale.is_cal_type === "individual";
  const items = sale.details || [];
  const hasBonusItems = items.some(isBonusItem);

  // HSN/SAC is optional on the item master -- show the column only when at
  // least one item on this voucher actually has a code.
  const showHSN = isIndia && isIndiaGst && items.some((d) => d.hsn_sac_code);

  const grandTotal = isIndividual
    ? items.reduce((sum, d) => {
        const qty = d.item_qty || d.sale_qty || 0;
        const rate = Number(d.item_rate || 0);
        let total = qty * rate;
        total -= (Number(d.item_discount_per) / 100) * total;
        total += (Number(d.item_tax_per) / 100) * total;
        return sum + total;
      }, 0)
    : sale.total_amount || 0;

  const accMgrLabel = isUAE ? "Rep." : "Account Manager";
  const taxRegLabel = isUAE ? "TRN No" : "Tax Reg No";

  const useBranch = !!authInfo?.branch_info;
  const logoUrl = useBranch ? institution?.branch_logo : institution?.logo;
  const companyName = (useBranch ? institution?.branch_name : institution?.name) || "Institution";

  const usedWidth =
    COL_WIDTH.hash + (showHSN ? COL_WIDTH.hsn : 0) + COL_WIDTH.rate + COL_WIDTH.qty +
    (isIndividual ? COL_WIDTH.disc : 0) + (isIndividual && !isGSTDisabled ? COL_WIDTH.tax : 0) + COL_WIDTH.amount;
  const itemMaxChars = Math.max(20, Math.floor((PAGE_CONTENT_WIDTH - usedWidth - CELL_PADDING) / CHAR_WIDTH_PX));

  const columns = [
    { label: "#", width: `${COL_WIDTH.hash}px` },
    { label: isIndividual ? "Item Name" : "Item", cellClassName: "da-item-td" },
  ];
  if (showHSN) columns.push({ label: "HSN/SAC", width: `${COL_WIDTH.hsn}px` });
  columns.push({ label: "Rate", align: "right", width: `${COL_WIDTH.rate}px` });
  columns.push({ label: "Quantity", align: "right", width: `${COL_WIDTH.qty}px` });
  if (isIndividual) {
    columns.push({ label: "Discount %", align: "right", width: `${COL_WIDTH.disc}px` });
    if (!isGSTDisabled) columns.push({ label: taxLabel, align: "right", width: `${COL_WIDTH.tax}px` });
    columns.push({ label: "Total", align: "right", width: `${COL_WIDTH.amount}px` });
  } else {
    columns.push({ label: "Amount", align: "right", width: `${COL_WIDTH.amount}px` });
  }

  const rows = items.map((d, i) => {
    const cells = [
      String(i + 1),
      <ItemCell key="n" name={d.item_name} maxChars={itemMaxChars} description={d.item_description} />,
    ];
    if (showHSN) cells.push(d.hsn_sac_code);
    cells.push(<span className="da-num-cell" key="r">{numberFormat(d.item_rate)}</span>);
    cells.push(<span className="da-num-cell" key="q">{formatPercent(d.item_qty)} {d.unit_symbol}</span>);

    let rawAmount;
    if (isIndividual) {
      cells.push(<span className="da-num-cell" key="d">{formatPercent(d.item_discount_per)}</span>);
      if (!isGSTDisabled) cells.push(<span className="da-num-cell" key="t">{formatPercent(d.item_tax_per)}</span>);
      const qty = Number(d.item_qty || d.sale_qty || 0);
      const rate = Number(d.item_rate || 0);
      let total = qty * rate;
      total -= (Number(d.item_discount_per) / 100) * total;
      total += (Number(d.item_tax_per) / 100) * total;
      rawAmount = total;
    } else {
      rawAmount = Number(d.item_total || 0);
    }
    cells.push(<span className="da-amount-cell" key="a">{numberFormat(rawAmount)}</span>);

    return { cells, isBonus: isBonusItem(d), rawAmount };
  });

  const totalsRows = [];
  if (!isIndividual) {
    totalsRows.push({ label: "Sub Total", value: numberFormat(sale.sub_total || 0) });
    totalsRows.push({ label: "Discount", value: `- ${numberFormat(sale.total_discount || 0)}`, credit: true });
    totalsRows.push({ label: "VAT", value: numberFormat(sale.total_tax || 0) });
  }

  const showBalanceDue = Number(accDue) > 0;
  const totalQty = items.reduce((sum, d) => sum + (Number(d.item_qty) || 0), 0);

  return {
    docTitle: "INVOICE",
    docNumber: sale.sale_voucher_no,
    institution: { logoUrl, companyName, companyGstin: null },
    party: {
      billToLabel: "Bill To",
      billTo: {
        name: sale.acc_name,
        lines: [
          `Address: ${sale.address}`,
          `Contact No: +${sale.contact_no}`,
          sale.acc_mgr ? `${accMgrLabel}: ${sale.acc_mgr}` : null,
          sale.vat_no ? `${taxRegLabel}: ${sale.vat_no}` : null,
        ].filter(Boolean),
      },
      shipTo:
        showShipping && sale.shipping_address
          ? {
              lines: [
                sale.shipping_contact_person ? `Contact Person: +${sale.shipping_contact_person}` : null,
                `Address: ${sale.shipping_address}`,
                sale.shipping_po_box ? `Po Box: ${sale.shipping_po_box}` : null,
                sale.shipping_contact_no ? `Contact No: +${sale.shipping_contact_no}` : null,
              ].filter(Boolean),
            }
          : null,
      fromLabel: "From",
      from: {
        name: companyName,
        lines: [
          institutionData?.street_address || null,
          institutionData?.phone_no ? `+${institutionData.phone_no}` : null,
          institutionData?.vat_license ? `${taxRegLabel}: ${institutionData.vat_license}` : null,
        ].filter(Boolean),
      },
      metaLabel: "Insurance & Invoice Details",
      meta: [
        { label: "Date", value: (formatToCompanyTimezone(sale.created_date) || "").split(" ")[0] },
        ...(showBalanceDue
          ? [
              { label: "Due Date", value: sale.due_date || "-" },
              { label: "Total Balance Due", value: numberFormat(accDue || 0) },
            ]
          : []),
        ...(sale.company_name ? [{ label: "Insurance Name", value: sale.company_name }] : []),
        ...(sale.copay_amount != null ? [{ label: "Insurance CoPay", value: `${numberFormat(sale.copay_amount || 0)} %` }] : []),
        ...(sale.insurance_amount != null ? [{ label: "Insurance Amt", value: numberFormat(sale.insurance_amount || 0) }] : []),
        ...(sale.patient_amt != null ? [{ label: "Customer Amt", value: numberFormat(sale.patient_amt || 0) }] : []),
        ...(sale.doctor_name ? [{ label: "Doctor", value: sale.doctor_name }] : []),
      ],
    },
    columns,
    rows,
    amountFn: (slice) => numberFormat(slice.reduce((sum, r) => sum + (r.rawAmount || 0), 0)),
    totalItems: items.length,
    totalQty,
    totalsRows,
    grandTotalLabel: `Grand Total (${currency || ""})`,
    grandTotalValue: numberFormat(grandTotal),
    paidValue: numberFormat(sale.paid_total || 0),
    dueValue: numberFormat(sale.due_total || 0),
    amountInWordsText: convertAmountToWordsDesignA(grandTotal, { country, currency }),
    bankDetails: bankDetails || null,
    taxSummary: null,
    terms: institutionData?.invoice_default_terms || sale.narration || "Thanks for your business.",
    signatory: {
      companyName,
      stampUrl: showStamp ? institutionData?.stamp : null,
      signatureUrl: showSignature ? institutionData?.signature : null,
    },
    bonusNote: hasBonusItems ? "*Bonus items are supplied free of cost and not included in taxable value." : null,
    rowBudgets: SALES_INSURANCE_ROW_BUDGETS,
    tableClassName: "da-table--rich-item",
  };
}
